import { useState } from "react";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Moon, Sparkles, Zap, RefreshCw, Droplet } from "lucide-react";
import { getSleep, addPeriod } from "../api";
import { toast } from "sonner";
import { todayISO } from "../lib/planner";

const MOODS = ["😞", "😕", "😐", "🙂", "😄"];
const MOTIV = ["🪫", "🔅", "🔆", "⚡", "🚀"];

const TapRow = ({ label, icon: Icon, options, value, onChange, testPrefix }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="h-4 w-4 text-stone-400" />
      <Label className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400">{label}</Label>
    </div>
    <div className="grid grid-cols-5 gap-2">
      {options.map((emoji, i) => {
        const v = i + 1;
        const active = value === v;
        return (
          <button
            key={v}
            type="button"
            data-testid={`${testPrefix}-tap-${v}`}
            onClick={() => onChange(v)}
            className={`min-h-[60px] rounded-2xl text-2xl transition-transform duration-150 active:scale-95 border ${
              active
                ? "bg-[#b1543b] border-[#b1543b] scale-105 shadow-md"
                : "bg-white dark:bg-stone-800 border-stone-100 dark:border-stone-700 hover:-translate-y-0.5"
            }`}
          >
            <span className={active ? "grayscale-0" : "grayscale opacity-70"}>{emoji}</span>
          </button>
        );
      })}
    </div>
  </div>
);

export const CheckInForm = ({ settings, onSaved, saving, initial }) => {
  const [cycleMode, setCycleMode] = useState("day");
  const [cycleDay, setCycleDay] = useState(initial?.cycle_day || 1);
  const [periodStart, setPeriodStart] = useState(settings?.last_period_start || "");
  const [sleep, setSleep] = useState(initial?.sleep_score ?? 70);
  const [mood, setMood] = useState(initial?.mood || 3);
  const [motivation, setMotivation] = useState(initial?.motivation || 3);
  const [fetchingSleep, setFetchingSleep] = useState(false);
  const [recovery, setRecovery] = useState(initial?.recovery || null);
  const [sleepSource, setSleepSource] = useState(initial?.sleep_source || "manual");

  const submit = () => {
    const payload = {
      sleep_score: Number(sleep),
      sleep_source: sleepSource,
      recovery,
      mood,
      motivation,
      supplements_taken: initial?.supplements_taken || [],
    };
    if (cycleMode === "day") payload.cycle_day = Number(cycleDay);
    else payload.last_period_start = periodStart;
    onSaved(payload);
  };

  const startNewPeriod = async () => {
    const t = todayISO();
    setCycleMode("date");
    setPeriodStart(t);
    setCycleDay(1);
    try {
      await addPeriod(t);
      toast.success("New cycle logged — you're on day 1 🌙");
    } catch {
      toast.error("Couldn't save the new cycle — tap 'Show me today's capacity' to record it");
    }
  };

  const pullSleep = async () => {
    setFetchingSleep(true);
    try {
      const data = await getSleep(initial?.date || new Date().toISOString().slice(0, 10));
      if (data.available && data.sleep_score != null) {
        setSleep(data.sleep_score);
        setRecovery(data.recovery || null);
        setSleepSource("ultrahuman");
        toast.success(`Sleep pulled from Ultrahuman${data.hours_slept ? ` · ${data.hours_slept}h` : ""}`);
      } else {
        toast.info("Ultrahuman not connected — keeping manual sleep entry");
      }
    } catch {
      toast.info("Ultrahuman unavailable — using manual sleep");
    }
    setFetchingSleep(false);
  };

  return (
    <div className="rounded-3xl bg-[#faf4ee] dark:bg-stone-900 border border-stone-100 dark:border-stone-800 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-7">
      <h2 className="font-heading text-2xl font-bold text-[#3b2a24] dark:text-stone-100">Morning check-in</h2>

      {/* Cycle */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400">Cycle</Label>
          <div className="flex gap-1 text-xs bg-stone-100 dark:bg-stone-800 rounded-full p-1">
            <button
              type="button"
              data-testid="cycle-mode-day"
              onClick={() => setCycleMode("day")}
              className={`px-3 py-1 rounded-full font-medium ${cycleMode === "day" ? "bg-white dark:bg-stone-700 shadow-sm text-[#3b2a24] dark:text-stone-100" : "text-stone-400"}`}
            >
              Cycle day
            </button>
            <button
              type="button"
              data-testid="cycle-mode-date"
              onClick={() => setCycleMode("date")}
              className={`px-3 py-1 rounded-full font-medium ${cycleMode === "date" ? "bg-white dark:bg-stone-700 shadow-sm text-[#3b2a24] dark:text-stone-100" : "text-stone-400"}`}
            >
              Period start
            </button>
          </div>
        </div>
        {cycleMode === "day" ? (
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="cycle-day-minus"
              onClick={() => setCycleDay((d) => Math.max(1, Number(d) - 1))}
              className="h-12 w-12 rounded-full bg-stone-100 dark:bg-stone-800 text-2xl text-stone-600 active:scale-95"
            >
              −
            </button>
            <Input
              data-testid="cycle-day-input"
              type="number"
              value={cycleDay}
              onChange={(e) => setCycleDay(e.target.value)}
              className="text-center text-2xl font-heading font-bold h-14 rounded-2xl border-stone-200"
            />
            <button
              type="button"
              data-testid="cycle-day-plus"
              onClick={() => setCycleDay((d) => Number(d) + 1)}
              className="h-12 w-12 rounded-full bg-stone-100 dark:bg-stone-800 text-2xl text-stone-600 active:scale-95"
            >
              +
            </button>
          </div>
        ) : (
          <Input
            data-testid="period-start-input"
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            className="h-14 rounded-2xl border-stone-200 text-lg"
          />
        )}
        <button
          type="button"
          data-testid="start-new-period"
          onClick={startNewPeriod}
          className="mt-3 w-full flex items-center justify-center gap-2 h-11 rounded-full border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold hover:-translate-y-0.5 transition-transform duration-200 active:scale-[0.98]"
        >
          <Droplet className="h-4 w-4" />
          Start new period today
        </button>
      </div>

      {/* Sleep */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Moon className="h-4 w-4 text-stone-400" />
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400">
              Sleep score · {sleep}
            </Label>
          </div>
          <button
            type="button"
            data-testid="pull-sleep-btn"
            onClick={pullSleep}
            disabled={fetchingSleep}
            className="flex items-center gap-1.5 text-xs font-medium text-stone-500 hover:text-[#3b2a24] dark:text-stone-100"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${fetchingSleep ? "animate-spin" : ""}`} />
            Ultrahuman
          </button>
        </div>
        <Slider
          data-testid="sleep-slider"
          value={[Number(sleep)]}
          onValueChange={(v) => setSleep(v[0])}
          max={100}
          step={1}
          className="py-2"
        />
      </div>

      <TapRow label="Mood" icon={Sparkles} options={MOODS} value={mood} onChange={setMood} testPrefix="mood" />
      <TapRow label="Motivation" icon={Zap} options={MOTIV} value={motivation} onChange={setMotivation} testPrefix="motivation" />

      <Button
        data-testid="submit-checkin"
        onClick={submit}
        disabled={saving}
        className="w-full h-14 rounded-full bg-[#b1543b] hover:bg-[#8f3f2b] dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-white text-base font-semibold"
      >
        {saving ? "Reading your day…" : "Show me today's capacity"}
      </Button>
    </div>
  );
};
